import { FormAnswer } from './formAnswers';

export interface ResumeStatistics {
  assignedTasks: number;
  completedTasks: number;
  formsAnswered: number;
  locationsVisited: number;
}

export enum RESUME_FILTERS_NAME {
  DATE_RANGE = 'dateRange',
  LOCATION = 'location',
  LOCATION_WITH_DESCENDANTS = 'locationWithDescendants',
  LOCATION_TYPE = 'locationType',
  FORM = 'form',
  STATUS = 'status',
  USER = 'user',
  FORM_CODE = 'formCode',
  QUESTION_AND_ANSWER = 'questionAndAnswer',
  FINDING = 'finding',
  SIGNATURE = 'signature',
  ANSWERED_FROM = 'answeredFrom',
  ASSETS = 'assets',
}

export enum RESUME_FILTERS_QUERY_PARAM {
  DATE_RANGE_FROM = 'start',
  DATE_RANGE_TO = 'end',
  LOCATION = 'location_id',
  LOCATION_WITH_DESCENDANTS = 'location_with_descendants_id',
  LOCATION_TYPE = 'location_type',
  FORM = 'task_form_id',
  STATUS = 'state',
  USER = 'user_id',
  FORM_CODE = 'code',
  QUESTION_AND_ANSWER_QUESTION = 'search_question_name',
  QUESTION_AND_ANSWER_ANSWER = 'search_value',
  ASSETS = 'asset_id',
}

export type ResumeFilterSignatureStatus =
  | 'mandatoryIncomplete'
  | 'mandatoryComplete'
  | 'optionalIncomplete'
  | 'optionalComplete';
export type ResumeFilterFindingStatus = 'includes' | 'without';
export type ResumeFilterAnsweredFromSource = 'web' | 'mobile';

export interface ResumeFilters {
  [RESUME_FILTERS_NAME.DATE_RANGE]: {
    from: Date;
    to: Date;
  };
  [RESUME_FILTERS_NAME.LOCATION]: number[] | null;
  [RESUME_FILTERS_NAME.LOCATION_WITH_DESCENDANTS]: number[] | null;
  [RESUME_FILTERS_NAME.LOCATION_TYPE]: 'nestable_locations' | 'locations' | null;
  [RESUME_FILTERS_NAME.FORM]: FormAnswer['form_id'][] | null;
  [RESUME_FILTERS_NAME.STATUS]: FormAnswer['state_id'][] | null;
  [RESUME_FILTERS_NAME.USER]: FormAnswer['user_id'][] | null;
  [RESUME_FILTERS_NAME.FORM_CODE]: FormAnswer['code'] | null;
  [RESUME_FILTERS_NAME.QUESTION_AND_ANSWER]: {
    question: string;
    answer: string;
  } | null;
  [RESUME_FILTERS_NAME.FINDING]: ResumeFilterFindingStatus | null;
  [RESUME_FILTERS_NAME.SIGNATURE]: ResumeFilterSignatureStatus | null;
  [RESUME_FILTERS_NAME.ANSWERED_FROM]: ResumeFilterAnsweredFromSource | null;
  [RESUME_FILTERS_NAME.ASSETS]: number[] | null;
}

export interface ResumeStatisticsResponse {
  assigned_tasks: number;
  completed_tasks: number;
  forms_answered: number;
  locations_visited: number;
}

export interface ResumeTaskFormsResponse {
  forms: ResumeTaskForm[];
  pages: number;
  page: number;
  /** Real total across all pages, not the page size. Optional because a backend
   * without it still serves the pre-existing consumers. */
  total_forms?: number;
}

export interface ResumeTaskForm {
  id: number;
  name: string;
}

export enum FormAnswerStatusColors {
  WHITE = '#f5f5f5',
  BLUE = '#d9edf7',
  YELLOW = '#fcf8e3',
  RED = '#f2dede',
  GREEN = '#dff0d8',
  GRAY = '#fafbff',
}

export enum FormAnswerStatusColorNames {
  WHITE = 'white',
  BLUE = 'blue',
  YELLOW = 'yellow',
  RED = 'red',
  GREEN = 'green',
  GRAY = 'gray',
}

export interface FormAnswerStatus {
  id: number;
  name: string;
  description: string;
  code: string;
  color: FormAnswerStatusColors;
  url: string;
  deleted?: boolean;
}

export type FormAnswerStatusMap = Record<FormAnswerStatus['id'], FormAnswerStatus>;

type FormAnswerChangeLogStatus = {
  id: number;
  name: string;
  color: string;
};

type FormAnswerChangeLogBaseEntry = {
  id: number;
  form_answer_id: number;
  user: string;
  created_at: string;
  updated_at: string;
};

type SimpleChangeLogEntry = FormAnswerChangeLogBaseEntry & {
  history_type:
    | 'duplicated'
    | 'form_created'
    | 'cleaned'
    | 'recovered'
    | 'deleted'
    | 'form_changed'
    | 'cleaned';
};

type StatusChangedChangeLogEntry = FormAnswerChangeLogBaseEntry & {
  history_type: 'status_changed';
  new_status: FormAnswerChangeLogStatus | null;
};

export type WebhookSyncIntegrationType = 'gsheet';

type WebhookSyncBaseEntry = {
  webhook_id: number;
  integration_type: WebhookSyncIntegrationType;
  last_event_at: string;
  created_at: string;
  updated_at: string;
};

// Translated copy for a gsheet sync error_id. Lives once per
// response in GsheetErrorCatalog; events reference it by error_id.
// The render layer picks the active locale via
// useUserDataStore().lang. Adding a new error_id is a pure backend
// change (errors.rb + ERRORS.md + integrations.google_sheet.sync_errors
// locale entries in en.yml/es.yml/pt.yml).
export interface GsheetErrorCopy {
  es: string;
  en: string;
  pt: string;
}

export interface GsheetErrorCatalogEntry {
  name: GsheetErrorCopy;
  description: GsheetErrorCopy;
}

// Response-level dictionary: error_id -> {name, description} per
// locale. Keyed off the same strings that show up in the error_ids
// array of each webhook_sync_error_set entry, deduped across the
// whole response.
export type GsheetErrorCatalog = Record<string, GsheetErrorCatalogEntry>;

type WebhookSyncErrorSetEntry = WebhookSyncBaseEntry & {
  history_type: 'webhook_sync_error_set';
  // BE also sends a `count` field equal to error_ids.length. We
  // intentionally don't surface it on this type — the FE derives it
  // from error_ids to avoid drift. Extra JSON props don't fail
  // structural typing.
  error_ids: string[];
};

type WebhookSyncRestoredEntry = WebhookSyncBaseEntry & {
  history_type: 'webhook_sync_restored';
};

type WebhookSyncOkEntry = WebhookSyncBaseEntry & {
  history_type: 'webhook_sync_ok_set';
};

export type FormAnswerChangeLogEntry =
  | StatusChangedChangeLogEntry
  | SimpleChangeLogEntry
  | WebhookSyncErrorSetEntry
  | WebhookSyncRestoredEntry
  | WebhookSyncOkEntry;

// Narrowed slices of the changelog union. The webhook-sync slice is
// what <WebhookSyncEntry> takes; the form-history slice is everything
// else and is used by the parent's row chrome + keying helpers.
export type WebhookSyncChangeLogEntry = Extract<
  FormAnswerChangeLogEntry,
  { history_type: 'webhook_sync_error_set' | 'webhook_sync_restored' | 'webhook_sync_ok_set' }
>;

export type FormHistoryChangeLogEntry = Exclude<
  FormAnswerChangeLogEntry,
  { history_type: `webhook_sync_${string}` }
>;

export type ResumeExportType = 'answers' | 'forms' | 'subforms' | 'pdfs' | 'pdfsZip';

export interface TaskAssignsStatsResponse {
  completed: number;
  total: number;
}

export interface PreviousPeriodCountAnswersResponse {
  form_answers_count: number;
  locations_count: number;
  total_signature_requests: number;
  completed_signature_requests: number;
}

export interface PreviousPeriodCountTaskAssignsResponse {
  task_assigns_count: number;
  completed_task_assigns_count: number;
}
